import { animated, useSpring } from '@react-spring/web'
import { useSelector } from 'react-redux'
import { motion } from 'framer-motion'

import { RootState } from '../../../store/store'

export default function PurchaseDteProgressAlert() {

  const { visible, current } = useSelector(
    (state: RootState) => state.purchaseDteRealtime
  )

  const { opacity, transform } = useSpring({

    opacity: visible ? 1 : 0,

    transform: visible
      ? 'translateY(0px)'
      : 'translateY(25px)',

    config: {
      tension: 220,
      friction: 22,
    },
  })

  if (!visible || !current) return null

  const progress = current.progress || 0

  // =========================================================
  // 🎨 THEMES
  // =========================================================

  const colors = {

    pending: {
      border: 'border-primary/20',
      text: 'text-primary',
      progress: 'bg-primary',
      badge: 'bg-primary text-white',
    },

    success: {
      border: 'border-[#3CA745]/20',
      text: 'text-[#3CA745]',
      progress: 'bg-[#3CA745]',
      badge: 'bg-[#3CA745] text-white',
    },

    error: {
      border: 'border-[#DC3545]/20',
      text: 'text-[#DC3545]',
      progress: 'bg-[#DC3545]',
      badge: 'bg-[#DC3545] text-white',
    },

    warning: {
      border: 'border-[#F9C107]/20',
      text: 'text-[#F9C107]',
      progress: 'bg-[#F9C107]',
      badge: 'bg-[#F9C107] text-[#212B36]',
    },

    info: {
      border: 'border-[#3BA2B8]/20',
      text: 'text-[#3BA2B8]',
      progress: 'bg-[#3BA2B8]',
      badge: 'bg-[#3BA2B8] text-white',
    },
  }

  const theme =
    colors[current.type as keyof typeof colors]
    || colors.info

  return (
    <animated.div
      style={{
        opacity,
        transform,
        pointerEvents: visible ? 'auto' : 'none',
      }}
      className="
        fixed
        bottom-4
        right-4
        z-[999999]
      "
    >
      <div
        className={`
          relative
          w-[320px]
          overflow-hidden
          rounded-2xl
          border
          bg-white/95
          shadow-4
          backdrop-blur-xl
          dark:border-strokedark
          dark:bg-boxdark/95
          ${theme.border}
        `}
      >

        {/* TOP ACCENT */}

        <div
          className={`
            absolute
            left-0
            top-0
            h-[3px]
            w-full
            ${theme.progress}
          `}
        />

        {/* CONTENT */}

        <div className="relative p-4">

          <div className="flex items-start gap-3">

            {/* STATUS DOT */}

            <div className="pt-1">

              <motion.div
                animate={{
                  scale: [1, 1.12, 1],
                  opacity: [1, 0.7, 1],
                }}
                transition={{
                  repeat: Infinity,
                  duration: 1.8,
                }}
                className={`
                  relative
                  h-3
                  w-3
                  rounded-full
                  ${theme.progress}
                `}
              >
                <div
                  className={`
                    absolute
                    inset-0
                    animate-ping
                    rounded-full
                    opacity-30
                    ${theme.progress}
                  `}
                />
              </motion.div>
            </div>

            {/* BODY */}

            <div className="min-w-0 flex-1">

              {/* HEADER */}

              <div
                className="
                  flex
                  items-start
                  justify-between
                  gap-2
                "
              >

                <div className="min-w-0">

                  <h4
                    className={`
                      truncate
                      text-[14px]
                      font-semibold
                      tracking-tight
                      ${theme.text}
                    `}
                  >
                    {current.title}
                  </h4>

                  <p
                    className="
                      mt-0.5
                      text-[11px]
                      leading-relaxed
                      text-bodydark2
                    "
                  >
                    {current.message}
                  </p>
                </div>

                {/* BADGE */}

                <div
                  className={`
                    shrink-0
                    rounded-full
                    px-2.5
                    py-0.5
                    text-[9px]
                    font-bold
                    uppercase
                    tracking-wide
                    ${theme.badge}
                    ${theme.text}
                  `}
                >
                  {current.status}
                </div>
              </div>

              {/* PROGRESS */}

              <div className="mt-3">

                <div
                  className="
                    mb-1.5
                    flex
                    items-center
                    justify-between
                  "
                >

                  <span
                    className="
                      text-[10px]
                      font-medium
                      text-bodydark2
                    "
                  >
                    Procesando...
                  </span>

                  <span
                    className={`
                      text-[10px]
                      font-semibold
                      ${theme.text}
                    `}
                  >
                    {progress}%
                  </span>
                </div>

                {/* BAR */}

                <div
                  className="
                    relative
                    h-1.5
                    overflow-hidden
                    rounded-full
                    bg-gray
                    dark:bg-meta-4
                  "
                >

                  <motion.div
                    className={`
                      relative
                      h-full
                      rounded-full
                      ${theme.progress}
                    `}
                    initial={{ width: 0 }}
                    animate={{ width: `${progress}%` }}
                    transition={{
                      duration: 0.4,
                      ease: 'easeOut',
                    }}
                  >

                    {/* SHIMMER */}

                    <motion.div
                      animate={{
                        x: ['-100%', '200%'],
                      }}
                      transition={{
                        repeat: Infinity,
                        duration: 1.4,
                        ease: 'linear',
                      }}
                      className="
                        absolute
                        inset-0
                        w-1/3
                        bg-white/25
                      "
                    />
                  </motion.div>
                </div>

                {/* FOOTER */}

                <div
                  className="
                    mt-2
                    flex
                    items-center
                    justify-between
                    text-[9px]
                    text-bodydark2
                  "
                >

                  <span>Sistema realtime</span>

                  <span>MH • DTE</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </animated.div>
  )
}